import { useMemo } from 'react';
import { isSameDay, subDays } from 'date-fns';
import { useLang } from 'hooks/lang';

import { Skeleton, Column, Row, Card, Dot } from 'components/Blocks';

import { getDateWithMonthText, formatDate, getTimeDisplay } from 'utils/date';

interface LogsProps {
  children?: never;
  loading?: boolean;
  logs: Array<{
    timestamp: string;
    text: string;
    id: string;
  }>;
}

export function Logs({ logs, loading }: LogsProps): JSX.Element {
  const { t } = useLang();

  const logGroups = useMemo(() => {
    const today = new Date();
    const yesterday = subDays(today, 1);

    const [todayLogs, yesterdayLogs] = [today, yesterday].map((date) =>
      logs.filter(({ timestamp }) => isSameDay(date, new Date(timestamp))),
    );

    const all = [...todayLogs, ...yesterdayLogs];

    return [
      {
        label: `${t('today')}: ${getDateWithMonthText(today)}`,
        logs: todayLogs,
      },
      {
        label: `${t('yesterday')}: ${getDateWithMonthText(yesterday)}`,
        logs: yesterdayLogs,
      },
      {
        label: t('previous-logs'),
        logs: logs.filter(({ id }) => !all.find((log) => log.id === id)),
      },
    ].filter(({ logs }) => logs.length);
  }, [logs, t]);

  if (loading) return <Skeleton width="100%" height={360} />;

  return (
    <Card
      flow="column"
      className="scrollbar h-full overflow-y-auto border-primary bg-[#f6fcff] dark:bg-accent"
    >
      {!logs.length ? (
        <span className="text-center">{t('no-logs-found')}</span>
      ) : (
        logGroups.map(({ label, logs }, index) => (
          <Column key={`log-group-${index}`}>
            <span className="self-start font-bold">{label}</span>

            <hr className="h-[1px] w-full" />

            <Column gap="3" className="phone:gap-4">
              {!logs.length && <span className="text-center">{t('no-logs-for-this-day-yet')}</span>}

              {logs.map(({ id, text, timestamp }) => (
                <Row key={id} alignItems="flex-start" className="phone:grid phone:grid-cols-2">
                  <Row>
                    <Dot color="primary" />

                    <span className="w-12 phone:text-sm phone:font-semibold">
                      {getTimeDisplay(timestamp)}
                    </span>
                  </Row>

                  <span className="phone:col-span-2 phone:text-justify phone:text-sm">{text}</span>

                  <span className="ml-auto text-sm phone:col-start-2 phone:row-start-1 phone:font-semibold">
                    {formatDate(timestamp)}
                  </span>
                </Row>
              ))}
            </Column>
          </Column>
        ))
      )}
    </Card>
  );
}
